import { useId, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getHeroMarkAsset, getWordmarkAsset } from "@/constants/assets";
import { getAccountComponentRoute, ROUTES } from "@/constants/navigation";
import { useUser } from "@/contexts/UserContext";
import { useDynamicSidebarItems } from "@/hooks/useDynamicSidebarItems";
import { useTheme } from "@/hooks/useTheme";

import { isSidebarItemVisible } from "@/components/Layout/account/sidebarPermissions";
import { translateUiText } from "@/interfaceBuilder/uiTranslations";
import { getUiSidebarItemComponentId } from "@/utils/uiComponentLinks";
import { resolvePictureUrn } from "@/utils/pictureUrn";
import type { AccountSidebarState } from "@/components/Layout/account/AccountLayout";
import type { StoredUiSidebarItem } from "@/storage/uiSidebarItemsRepository";
import "./AccountSidebar.css";

type AccountSidebarIconName =
  | "component"
  | "realm"
  | "node"
  | "element"
  | "elementStore"
  | "finance"
  | "home"
  | "users"
  | "permissions"
  | "roles"
  | "collapse"
  | "expand";

// The icons a builder-made menu item can pick from. Keep in sync with the
// ecosystem's SIDEBAR_ITEM_ICON_OPTIONS.
const SIDEBAR_ITEM_ICONS: readonly AccountSidebarIconName[] = [
  "component",
  "home",
  "realm",
  "node",
  "element",
  "elementStore",
  "finance",
  "users",
  "roles",
  "permissions",
];

interface AccountSidebarProps {
  state: AccountSidebarState;
  onToggle: () => void;
}

export function AccountSidebar({ state, onToggle }: AccountSidebarProps) {
  const { i18n, t } = useTranslation();
  const { theme } = useTheme();
  const { session } = useUser();
  const dynamicSidebarItems = useDynamicSidebarItems({
    isAuthenticated: session.isAuthenticated,
    userId: session.user?.uuid,
    projectId: session.projectId,
  });
  // A builder-made item names the permissions it needs, and an account that
  // does not hold them is not shown it — the same rule the menu above follows,
  // read from the same introspection.
  const visibleDynamicItems = dynamicSidebarItems.items.filter((item) =>
    isSidebarItemVisible(item.props, session.permissions),
  );
  // A builder-made item names the heading it is drawn under, and the items
  // naming the same one are shown together beneath it.
  const dynamicSidebarBlocks = orderDynamicSidebarBlocks(
    visibleDynamicItems,
    i18n?.resolvedLanguage,
  );
  const isCollapsed = state === "collapsed";
  const sidebarClassName = `account-sidebar ${
    isCollapsed ? "account-sidebar--collapsed" : ""
  }`;
  const toggleLabel = isCollapsed
    ? t("accountLayout.expand")
    : t("accountLayout.collapse");

  return (
    <aside
      className={sidebarClassName}
      aria-label={t("accountLayout.navLabel")}
    >
      <NavLink to={ROUTES.home} className="account-sidebar__brand" aria-label={t("brand")}>
        {/* The wordmark gives way to the brand mark once the sidebar is too
            narrow for it: collapsed, or on a narrow viewport. */}
        <img
          src={getHeroMarkAsset(theme)}
          alt=""
          aria-hidden="true"
          className="account-sidebar__mark"
          width={32}
          height={32}
        />
        <img
          src={getWordmarkAsset(theme)}
          alt={t("brand")}
          className="account-sidebar__logo"
          width={151}
          height={32}
        />
      </NavLink>
      <nav className="account-sidebar__nav" aria-label={t("accountLayout.navLabel")}>
        {/* The home page, the element store and the billing page are built
            with the constructor and installed as manifests, so they arrive here
            as builder-made items rather than as menu entries of this file. They
            sit where the positions their manifests give them put them. */}
        {dynamicSidebarBlocks.map((block) => (
          <DynamicSidebarGroup
            key={block.key}
            block={block}
            isCollapsed={isCollapsed}
            language={i18n?.resolvedLanguage}
          />
        ))}
      </nav>

      <button
        type="button"
        className="account-sidebar__toggle"
        aria-label={toggleLabel}
        title={isCollapsed ? toggleLabel : undefined}
        onClick={onToggle}
      >
        <span className="account-sidebar__toggle-label">{toggleLabel}</span>
        <span className="account-sidebar__toggle-icon" aria-hidden="true">
          <AccountSidebarIcon name={isCollapsed ? "expand" : "collapse"} />
        </span>
      </button>
    </aside>
  );
}

interface DynamicSidebarBlock {
  key: string;
  name: string | null;
  items: StoredUiSidebarItem[];
}

function DynamicSidebarGroup({
  block,
  isCollapsed,
  language,
}: {
  block: DynamicSidebarBlock;
  isCollapsed: boolean;
  language: string | undefined;
}) {
  const { t } = useTranslation();
  const itemsId = useId();
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div
      className={block.name
        ? "account-sidebar__group account-sidebar__dynamic"
        : "account-sidebar__dynamic"}
      role="group"
      aria-label={block.name ?? t("accountLayout.dynamicNavLabel")}
    >
      {block.name ? (
        <button
          type="button"
          className="account-sidebar__group-label"
          aria-expanded={isExpanded}
          aria-controls={itemsId}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          <span>{block.name}</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d={isExpanded ? "m6 9 6 6 6-6" : "m9 6 6 6-6 6"} />
          </svg>
        </button>
      ) : null}
      <div
        id={itemsId}
        className="account-sidebar__group-items"
        hidden={!isCollapsed && !isExpanded}
      >
        {block.items.map((item) => (
          <DynamicSidebarLink
            key={item.uuid}
            item={item}
            isCollapsed={isCollapsed}
            language={language}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The builder-made items in the order the sidebar draws them: the order their
 * positions put them in, with the items naming the same group drawn together
 * -- under its name, read the way the visitor reads it -- where the first of
 * them stands. An item naming no group keeps its own place among them, so a
 * page positioned after a group is drawn after that group.
 */
function orderDynamicSidebarBlocks(
  items: readonly StoredUiSidebarItem[],
  language: string | undefined,
): DynamicSidebarBlock[] {
  const blocks: DynamicSidebarBlock[] = [];
  const groups = new Map<string, DynamicSidebarBlock>();

  items.forEach((item) => {
    const name = (translateUiText(item.props, "group", language) ?? "").trim();
    if (!name) {
      const previous = blocks.at(-1);
      if (previous && previous.name === null) {
        previous.items.push(item);
        return;
      }

      blocks.push({ key: `ungrouped:${item.uuid}`, name: null, items: [item] });
      return;
    }

    const group = groups.get(name);
    if (group) {
      group.items.push(item);
      return;
    }

    const block = { key: `group:${name}`, name, items: [item] };
    groups.set(name, block);
    blocks.push(block);
  });

  return blocks;
}

function DynamicSidebarLink({
  item,
  isCollapsed,
  language,
}: {
  item: StoredUiSidebarItem;
  isCollapsed: boolean;
  language: string | undefined;
}) {
  const componentId = getUiSidebarItemComponentId(item);
  // An item names itself in the language the visitor reads in, the way the
  // page behind it does.
  const name = translateUiText(item.props, "name", language) ?? item.name;
  const iconPicture = readSidebarItemPicture(item.props);

  return (
    <NavLink
      to={getAccountComponentRoute(componentId)}
      className={({ isActive }) => (
        [
          "account-sidebar__item",
          "account-sidebar__item--dynamic",
          isActive ? "account-sidebar__item--active" : "",
        ].filter(Boolean).join(" ")
      )}
      aria-label={isCollapsed ? name : undefined}
      title={isCollapsed ? name : undefined}
      data-ui-sidebar-item-id={item.uuid}
      data-ui-component-id={componentId}
    >
      <span className="account-sidebar__item-icon" aria-hidden="true">
        {iconPicture ? (
          <img
            alt=""
            className="account-sidebar__item-picture"
            src={iconPicture}
          />
        ) : (
          <AccountSidebarIcon name={readSidebarItemIcon(item.props)} />
        )}
      </span>
      <span className="account-sidebar__item-label">{name}</span>
    </NavLink>
  );
}

// An item draws a picture of its own when its icon is a URN, the way a picture
// component carries one: an element installed into the menu brings its own
// logo rather than picking from the glyphs this client ships.
function readSidebarItemPicture(
  props: Record<string, unknown> | undefined,
): string | null {
  const icon = props?.icon;

  return typeof icon === "string" ? resolvePictureUrn(icon) : null;
}

// A menu item without an icon of its own, or with one this client does not
// know, keeps the generic component icon.
function readSidebarItemIcon(
  props: Record<string, unknown> | undefined,
): AccountSidebarIconName {
  const icon = props?.icon;

  return SIDEBAR_ITEM_ICONS.find((name) => name === icon) ?? "component";
}

function AccountSidebarIcon({ name }: { name: AccountSidebarIconName }) {
  if (name === "component") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M8 9h8M8 13h5" />
      </svg>
    );
  }

  if (name === "realm") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="4" width="18" height="7" rx="1.5" />
        <rect x="3" y="13" width="18" height="7" rx="1.5" />
        <path d="M7 7.5h.01M7 16.5h.01" />
      </svg>
    );
  }

  if (name === "element") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M7 3h7l3 3v15H7z" />
        <path d="M14 3v4h4M10 12h4M10 16h4" />
      </svg>
    );
  }

  if (name === "elementStore") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M4 7h16v13H4z" />
        <path d="M2 7l2-4h16l2 4M8 11h8" />
      </svg>
    );
  }

  if (name === "node") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="7" />
        <path d="M12 5v14M5 12h14" />
      </svg>
    );
  }

  if (name === "expand") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M7 7l5 5-5 5" />
        <path d="M13 7l5 5-5 5" />
      </svg>
    );
  }

  if (name === "finance") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 10h18M7 15h3" />
      </svg>
    );
  }

  if (name === "home") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M3 11l9-8 9 8" />
        <path d="M5 10v10h14V10" />
      </svg>
    );
  }

  if (name === "users") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="9" cy="8" r="3.5" />
        <path d="M2.5 20c0-3 2.5-5 6.5-5s6.5 2 6.5 5" />
        <path d="M16 8a3 3 0 1 1 1 5.8" />
        <path d="M19 15.5c1.6.7 2.5 2 2.5 4" />
      </svg>
    );
  }

  if (name === "permissions") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M9 12l2 2 4-4" />
        <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z" />
      </svg>
    );
  }

  if (name === "roles") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="9" r="5" />
        <path d="M8.5 13.5L12 21l3.5-7.5" />
        <path d="M12 6.5h.01" />
      </svg>
    );
  }

  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17 7l-5 5 5 5" />
      <path d="M11 7l-5 5 5 5" />
    </svg>
  );
}
